// ✅ src/pages/Products.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AllBlogsThunk,
  DeleteBlogsThunk,
} from "../../redux/states/blogs/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { Box, Delete, Edit, Eye, Plus, Search, Trash } from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Blogs = () => {
  const dispatch = useDispatch();
  const { blogs, loading, error } = useSelector((state) => state.blogs);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL


  useEffect(() => {
    dispatch(AllBlogsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (blogs && blogs.length > 0) {
      const filtered = blogs?.filter(
        (product) =>
          product?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
          product.category_id_data
            ?.toLowerCase()
            .includes(searchTerm?.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [blogs, searchTerm]);

  const handleDeleteClick = (productId) => {
    setSelectedId(productId);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(DeleteBlogsThunk(selectedId));
      setModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleEdit = (uuid) => {
    navigate(`/update-new-blogs/${uuid}`);
  };

  const handleView = (uuid) => {
    navigate(`/view-blogs/${uuid}`);
  };

  const handleCreate = () => {
    navigate("/add-new-blogs");
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen  p-3">
        {/* Header Section */}
        <div className="mb-3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                  Blogs
                </h1>
                <p className="text-gray-600 mt-2">Total Blogs : {filteredProducts.length}</p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={handleCreate}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  <Plus className="mr-2" />
                  New Blog
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Stats Section */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-3">
          <div className="lg:col-span-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-4 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg shadow-sm transition-all duration-300"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 text-lg">Loading....</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8 transform transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-red-100 p-2 rounded-lg mr-4">
                  <Trash className="text-red-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-red-800 font-semibold text-lg">Error</h3>
                  <p className="text-red-600">{error}</p>
                </div>
              </div>
              <button className="text-red-600 hover:text-red-800">✕</button>
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {filteredProducts && filteredProducts?.length > 0
            ? filteredProducts.map((item, index) => (
                <div
                  key={item._id || index}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                >
                  {/* Product Image */}
                  <div className="relative overflow-hidden">
                    <img
                      src={`${API_URL || "/assets/laxmisamgri.png"}/${
                        item.image
                      }`}
                      alt={item.name}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src =  "/assets/laxmisamgri.png";
                      }}
                    />
                    <div className="absolute top-3 right-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          item.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>
                    <div className="absolute top-10 right-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          item.status === "active"
                            ? "bg-green-100 text-pink-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        Views:{item.views}
                      </span>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-5">
                    <h3 className="font-bold text-gray-800 text-lg mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {item?.name}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {item?.slug || "No Data"}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleView(item?.uuid)}
                        className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-xl font-medium flex items-center justify-center transition-all duration-300 group/btn"
                        title="व्यू डिटेल्स"
                      >
                        <Eye className="mr-2 group-hover/btn:scale-110 transition-transform" />
                      </button>
                      <button
                        onClick={() => handleEdit(item?.uuid)}
                        className="flex-1 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 px-3 py-2 rounded-xl font-medium flex items-center justify-center transition-all duration-300 group/btn"
                        title="एडिट प्रोडक्ट"
                      >
                        <Edit className="mr-2 group-hover/btn:scale-110 transition-transform" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(item?.uuid)}
                        className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 rounded-xl font-medium flex items-center justify-center transition-all duration-300 group/btn"
                        title="डिलीट प्रोडक्ट"
                      >
                        <Delete className="mr-2 group-hover/btn:scale-110 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            : !loading && (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
                  <div className="bg-gray-100 p-8 rounded-2xl mb-6">
                    <Box className="text-6xl text-gray-400 mx-auto" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-600 mb-2">
                    No blogs Found
                  </h3>
                  <button
                    onClick={handleCreate}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold flex items-center transition-all duration-300 transform hover:scale-105"
                  >
                    <Plus className="mr-2" />
                    Add Your First blogs
                  </button>
                </div>
              )}
        </div>
      </div>
      <ConfirmDeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Product"
        message="Do you really want to delete this product? This action cannot be undone."
      />
    </NonAuthLayout>
  );
};

export default Blogs;
